import { useEffect, useSyncExternalStore } from "react";
import { safeErrorCode } from "./safeErrorCode";

/**
 * Whether the database behind this build has project status yet.
 *
 * The app and franklin-data-pipeline deploy independently, so a build can be
 * live against a database the `project_status` migration has not reached.
 * Rather than a build flag someone has to remember to flip per environment,
 * the app asks the database once: selecting the column fails with 42703
 * (undefined column) until the migration applies. Until the answer is in —
 * and for good when the column is absent — status is dormant: queries leave
 * the column out, every project reads active, and no status UI renders. Once
 * the migration lands, the next page load lights the feature up by itself.
 */

type Availability = boolean | null; // null = not known yet

let availability: Availability = null;
let inFlight: Promise<boolean> | null = null;
let retryAfter = 0;
let hadTransientFailure = false;
const listeners = new Set<() => void>();
const recoveryListeners = new Set<() => void>();

/** PostgREST's code for a select naming a column the table does not have. */
const UNDEFINED_COLUMN = "42703";
const MISSING_COLUMN = "PGRST204";
const RETRY_COOLDOWN_MS = 30_000;

const isMissingColumn = (code: string) => code === UNDEFINED_COLUMN || code === MISSING_COLUMN;

function settle(value: boolean): boolean {
  availability = value;
  listeners.forEach((listener) => listener());
  if (value && hadTransientFailure) {
    hadTransientFailure = false;
    recoveryListeners.forEach((listener) => listener());
  }
  return value;
}

async function probe(): Promise<boolean> {
  let failureKind: "query_error" | "exception" = "exception";
  let errorCode = "unknown";
  try {
    // Imported on first probe rather than at module load: this module is
    // pulled in by the test setup, before any test file mocks the client.
    const { supabase } = await import("@/integrations/supabase/client");
    const { error } = await supabase.from("building_projects").select("project_status").limit(1);
    if (!error) return settle(true);
    if (isMissingColumn(safeErrorCode(error))) return settle(false);
    failureKind = "query_error";
    errorCode = safeErrorCode(error);
  } catch (error) {
    errorCode = safeErrorCode(error);
    if (isMissingColumn(errorCode)) return settle(false);
  }
  hadTransientFailure = true;
  retryAfter = Date.now() + RETRY_COOLDOWN_MS;
  const reportableError = new Error(`Project status availability probe failed (${failureKind})`);
  let sentryEventId: string | undefined;
  try {
    const Sentry = await import("@sentry/react");
    sentryEventId = Sentry.captureException(reportableError, {
      tags: {
        area: "projects",
        operation: "project_status_availability_probe",
        failure_kind: failureKind,
        error_code: errorCode,
      },
      contexts: {
        project_status_probe: { table: "building_projects", column: "project_status" },
      },
      fingerprint: ["projects", "project-status", "availability-probe"],
    });
  } catch {
    // Reporting is best effort; the availability check must still resolve.
  }
  try {
    const { logger } = await import("@/utils/logger");
    void logger
      .error(
        "database",
        "project_status_availability_probe",
        "Failed to determine project status availability",
        reportableError,
        { failure_kind: failureKind, error_code: errorCode, sentry_event_id: sentryEventId }
      )
      .catch(() => {
        // Log persistence is independent of the availability fallback.
      });
  } catch {
    // A logger failure must not block project queries.
  }
  return false;
}

/**
 * Resolve the availability, probing the database on first use. Repository
 * reads await this before deciding whether to select `project_status`.
 */
export function ensureProjectStatusAvailable(): Promise<boolean> {
  if (availability !== null) return Promise.resolve(availability);
  if (inFlight) return inFlight;
  if (Date.now() < retryAfter) return Promise.resolve(false);
  const pending = probe();
  inFlight = pending;
  const clearInFlight = () => {
    if (inFlight === pending) inFlight = null;
  };
  void pending.then(clearInFlight, clearInFlight);
  return pending;
}

/** Synchronous read of the settled answer — false while it is still unknown. */
export function isProjectStatusEnabled(): boolean {
  return availability === true;
}

/** Refresh caches built with the active fallback after a failed probe recovers. */
export function onProjectStatusRecovery(listener: () => void): () => void {
  recoveryListeners.add(listener);
  return () => recoveryListeners.delete(listener);
}

/** Re-renders the caller once the probe settles, and starts it if needed. */
export function useProjectStatusEnabled(): boolean {
  const enabled = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    isProjectStatusEnabled,
    isProjectStatusEnabled
  );
  useEffect(() => {
    void ensureProjectStatusAvailable();
  }, []);
  return enabled;
}

/**
 * Pin the answer — for tests, which have no database to ask. `null` returns
 * to "unknown", so the next use probes again.
 */
export function setProjectStatusAvailability(value: Availability): void {
  availability = value;
  inFlight = null;
  retryAfter = 0;
  hadTransientFailure = false;
  listeners.forEach((listener) => listener());
}
